import React, { useEffect } from 'react';
import ArchiveRestore from 'lucide-react/dist/esm/icons/archive-restore';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles';
import { Stars } from '../leads/LeadCard';
import {
  Table, Thead, Tbody, Tr, Th, Td,
  Button, Spinner, EmptyState, Badge, Pagination,
} from 'components/ui';
import { useSalesStore } from '../../stores/salesStore';
import { useUIStore } from 'stores';
import { getTranslation } from 'i18n';
import { STATUS_CONFIG } from '../../types/sales';

const ArchivedLeads: React.FC = () => {
  const {
    archivedLeads, archivedTotal, archivedPage, archivedLoading,
    fetchArchivedLeads, toggleArchive, openLeadModal,
  } = useSalesStore();
  const language = useUIStore((s) => s.language);
  const t = (key: string) => getTranslation(language, key);

  useEffect(() => {
    fetchArchivedLeads();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const perPage = 50;
  const totalPages = Math.ceil(archivedTotal / perPage);

  const handlePageChange = (page: number) => {
    fetchArchivedLeads(page);
  };

  if (archivedLoading && archivedLeads.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  if (archivedLeads.length === 0) {
    return (
      <EmptyState
        title={t('salesArchivedEmpty')}
        description={t('salesArchivedEmptyDesc')}
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-content-secondary">
          {archivedTotal !== 1
            ? t('salesArchivedCountPlural').replace('{{count}}', String(archivedTotal))
            : t('salesArchivedCount').replace('{{count}}', String(archivedTotal))}
        </div>
      </div>

      <Table>
        <Thead>
          <Tr>
            <Th>{t('salesArchivedColCompany')}</Th>
            <Th>{t('salesArchivedColStatus')}</Th>
            <Th>{t('salesArchivedColAiRating')}</Th>
            <Th>{t('salesArchivedColSalesRating')}</Th>
            <Th>{t('salesArchivedColCreated')}</Th>
            <Th></Th>
          </Tr>
        </Thead>
        <Tbody>
          {archivedLeads.map((lead) => (
            <Tr
              key={lead.id}
              className="cursor-pointer hover:bg-surface-primary transition-colors"
              onClick={() => openLeadModal(lead.id)}
            >
              <Td>
                <div>
                  <div className="text-sm font-medium text-content flex items-center gap-1.5">
                  {lead.source === 'ai_generated' && <Sparkles size={12} className="text-purple-400" />}
                  {lead.company_name}
                </div>
                  {lead.contact_name && (
                    <div className="text-xs text-content-muted">{lead.contact_name}</div>
                  )}
                </div>
              </Td>
              <Td>
                <Badge variant="neutral" size="sm">
                  {STATUS_CONFIG[lead.status] ? t(STATUS_CONFIG[lead.status].labelKey) : lead.status}
                </Badge>
              </Td>
              <Td>
                <Stars count={lead.ai_rating} size={12} color="purple" />
              </Td>
              <Td>
                <Stars count={lead.sales_rating} size={12} color="amber" />
              </Td>
              <Td>
                <span className="text-xs text-content-muted">
                  {new Date(lead.created_at).toLocaleDateString()}
                </span>
              </Td>
              <Td>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleArchive(lead.id);
                  }}
                  leftIcon={<ArchiveRestore size={14} />}
                >
                  {t('salesArchivedBtnUnarchive')}
                </Button>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>

      {totalPages > 1 && (
        <div className="mt-4">
          <Pagination
            page={archivedPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default ArchivedLeads;
